import React, { useState } from "react";
import { cn } from "@/lib/utils";

interface NumberStepperProps {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  disabled?: boolean;
}

export function NumberStepper({
  value,
  onValueChange,
  min = 0,
  max = 999,
  step = 1,
  className,
  disabled = false,
}: NumberStepperProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value.toString());

  const handleIncrement = () => {
    if (!disabled && value < max) {
      onValueChange(value + step);
    }
  };

  const handleDecrement = () => {
    if (!disabled && value > min) {
      onValueChange(value - step);
    }
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsEditing(true);
      setInputValue(value.toString());
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    const numValue = parseInt(inputValue) || min;
    const clampedValue = Math.min(Math.max(numValue, min), max);
    onValueChange(clampedValue);
    setIsEditing(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleInputBlur();
    } else if (e.key === "Escape") {
      setInputValue(value.toString());
      setIsEditing(false);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      handleIncrement();
      setIsEditing(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      handleDecrement();
      setIsEditing(false);
    }
  };

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-[6px] rounded-md py-[6px]",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      {isEditing ? (
        <input
          min={min}
          max={max}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          className="m-0 w-[30px] min-w-[20px] border-none bg-transparent p-0 text-center text-sm font-medium text-neutral-800 outline-none focus:border-0 focus:bg-transparent focus:ring-0"
          autoFocus
          onFocus={(e) => e.target.select()}
        />
      ) : (
        <span
          className="m-0 w-[30px] min-w-[20px] cursor-pointer p-0 text-center text-sm font-medium text-neutral-800 hover:bg-neutral-100"
          onClick={handleInputClick}
        >
          {value}
        </span>
      )}
      <div className="flex flex-col -space-y-1">
        <button
          onClick={handleIncrement}
          disabled={disabled || value >= max}
          className={cn(
            "flex h-auto w-auto items-center justify-center p-0",
            "disabled:cursor-not-allowed disabled:opacity-30",
          )}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M8 2L14 10H2L8 2Z" fill="rgba(107,95,80,1)" />
          </svg>
        </button>
        <button
          onClick={handleDecrement}
          disabled={disabled || value <= min}
          className={cn(
            "flex h-auto w-auto items-center justify-center p-0",
            "disabled:cursor-not-allowed disabled:opacity-30",
          )}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M8 14L2 6H14L8 14Z" fill="rgba(107,95,80,1)" />
          </svg>
        </button>
      </div>
    </div>
  );
}
